// Financial Service - Student payments and bank transfers
// Handles financial accounts, payments, and bank transfer submissions

import { apiGet, apiPostMultipart, API_BASE, getAuthHeaders } from '@/utils/api';

/**
 * Download the official payment receipt (reçu) PDF for one of the payer's own
 * payments (student = own, parent = linked child). Authenticated blob download.
 * Throws on non-OK responses so callers can surface an error toast.
 */
export async function downloadMyReceipt(paymentId: string, fileName?: string): Promise<void> {
  const response = await fetch(`${API_BASE}/financial/accounts/payments/${paymentId}/receipt`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    throw new Error('Reçu indisponible pour ce paiement');
  }
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName || `recu-${paymentId}.pdf`;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

// ==================== Student Financial Accounts ====================

export interface PaymentInstallment {
  id: string;
  sequenceNumber: number;
  label: string;
  dueDate: string;
  amountDue: number;
  amountPaid: number;
  status: 'PENDING' | 'PAID' | 'PARTIAL' | 'OVERDUE' | 'CANCELLED' | 'REFUNDED';
  paidAt?: string;
}

export interface PaymentSchedule {
  id: string;
  templateId: string;
  baseTuition: number;
  adjustedTuition: number;
  template?: {
    id: string;
    name: string;
    type: string;
  };
  installments: PaymentInstallment[];
}

export interface StudentDiscount {
  id: string;
  discountAmount: number;
  status: string;
  notes?: string;
  discountConfig?: {
    id: string;
    name: string;
    code: string;
  };
}

export type FeeItemType =
  | 'REGISTRATION'
  | 'TUITION'
  | 'LIBRARY'
  | 'LABORATORY'
  | 'INSURANCE'
  | 'UNIFORM'
  | 'TRANSPORT'
  | 'MEALS'
  | 'ACCOMMODATION'
  | 'MATERIALS'
  | 'EXAM'
  | 'OTHER';

export interface StudentFeeItem {
  id: string;
  accountId: string;
  label: string;
  type: FeeItemType;
  amount: number;
  description?: string;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
}

export interface AcademicYear {
  id: string;
  name: string;
  isCurrent: boolean;
  startDate: string;
  endDate: string;
}

export interface StudentFinancialAccount {
  id: string;
  studentId: string;
  academicYearId: string;
  totalDue: number;
  totalPaid: number;
  totalDiscount: number;
  balance: number;
  isBlocked: boolean;
  blockedAt?: string;
  blockedReason?: string;
  academicYear?: AcademicYear;
  schedule?: PaymentSchedule;
  discounts?: StudentDiscount[];
  feeItems?: StudentFeeItem[];
  payments?: StudentPayment[];
  student?: {
    id: string;
    firstName: string;
    lastName: string;
    studentCode: string;
    filiereInscrit: string;
    niveauFormation: number;
  };
}

export interface CurrentAccountResponse {
  hasAccount: boolean;
  message?: string;
  account?: StudentFinancialAccount;
  academicYear?: AcademicYear;
  student?: {
    id: string;
    firstName: string;
    lastName: string;
    studentCode: string;
    filiereInscrit: string;
    niveauFormation: number;
  };
}

/**
 * Fetch all financial accounts for the current student (all academic years)
 */
export async function fetchMyFinancialAccounts(): Promise<StudentFinancialAccount[]> {
  return apiGet<StudentFinancialAccount[]>('/financial/accounts/my-accounts');
}

/**
 * Fetch the current academic year's financial account for the student
 */
export async function fetchMyCurrentAccount(): Promise<CurrentAccountResponse> {
  return apiGet<CurrentAccountResponse>('/financial/accounts/my-current-account');
}

/**
 * Fetch a single financial account by ID (kept for compatibility)
 */
export async function fetchFinancialAccount(id: string): Promise<StudentFinancialAccount> {
  return apiGet<StudentFinancialAccount>(`/financial/accounts/${id}`);
}

/**
 * Admin/staff: fetch a student's financial accounts (all years) by Student id.
 * Used to show the student's frais financiers inside the student profile.
 * Requires FINANCE_PAYMENT_VIEW.
 */
export async function adminFetchStudentAccounts(studentId: string): Promise<StudentFinancialAccount[]> {
  return apiGet<StudentFinancialAccount[]>(`/financial/payments/accounts/${studentId}`);
}

// ==================== Student Payments ====================

export interface PaymentAllocation {
  id: string;
  amount: number;
  installment: {
    id: string;
    label: string;
    dueDate: string;
  };
}

export interface StudentPayment {
  id: string;
  accountId: string;
  amount: number;
  paymentDate: string;
  method: 'CASH' | 'CHECK' | 'BANK_TRANSFER' | 'CARD' | 'ONLINE';
  reference?: string;
  receiptNumber?: string;
  receiptUrl?: string;
  notes?: string;
  status: 'PENDING' | 'PAID' | 'PARTIAL' | 'CANCELLED' | 'REFUNDED';
  recordedBy?: string;
  /** Display name of the staff member who recorded the payment / issued the reçu. */
  recordedByName?: string | null;
  cancelledAt?: string;
  cancelledBy?: string;
  cancellationReason?: string;
  refundedAmount?: number;
  refundedAt?: string;
  refundedBy?: string;
  refundReason?: string;
  createdAt: string;
  allocations?: PaymentAllocation[];
  academicYear?: AcademicYear;
}

export interface MyPaymentsResponse {
  payments: StudentPayment[];
  groupedByYear: Record<string, StudentPayment[]>;
  total: number;
}

/**
 * Fetch all payments for the current student (all academic years)
 */
export async function fetchMyPayments(): Promise<MyPaymentsResponse> {
  return apiGet<MyPaymentsResponse>('/financial/accounts/my-payments');
}

// ==================== Bank Transfers ====================

export interface PendingBankTransfer {
  id: string;
  accountId: string;
  amount: number;
  transferDate: string;
  receiptUrl: string;
  reference?: string;
  bankName?: string;
  notes?: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  submittedAt: string;
  submittedBy: string;
  submittedByRole: string;
  reviewedBy?: string;
  reviewedAt?: string;
  reviewNotes?: string;
  rejectionReason?: string;
  account?: StudentFinancialAccount;
}

/**
 * Fetch all bank transfers for the current student
 */
export async function fetchMyBankTransfers(): Promise<PendingBankTransfer[]> {
  return apiGet<PendingBankTransfer[]>('/financial/bank-transfers/my-transfers');
}

/**
 * Submit a bank transfer receipt
 */
export async function submitBankTransfer(data: {
  accountId: string;
  /** Required when a parent or staff member submits on behalf of a student */
  studentId?: string;
  amount: number;
  transferDate: string;
  reference?: string;
  bankName?: string;
  notes?: string;
  receipt: File;
}): Promise<PendingBankTransfer> {
  const formData = new FormData();
  formData.append('accountId', data.accountId);
  if (data.studentId) formData.append('studentId', data.studentId);
  formData.append('amount', data.amount.toString());
  formData.append('transferDate', data.transferDate);
  if (data.reference) formData.append('reference', data.reference);
  if (data.bankName) formData.append('bankName', data.bankName);
  if (data.notes) formData.append('notes', data.notes);
  formData.append('receipt', data.receipt);

  return apiPostMultipart<PendingBankTransfer>('/financial/bank-transfers', formData);
}

/**
 * Get transfer by ID
 */
export async function fetchBankTransferById(id: string): Promise<PendingBankTransfer> {
  return apiGet<PendingBankTransfer>(`/financial/bank-transfers/${id}`);
}

// ==================== Currency Formatting ====================

/**
 * Format amount as Moroccan Dirham
 */
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('fr-MA', {
    style: 'currency',
    currency: 'MAD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

/**
 * Format payment method label
 */
export function formatPaymentMethod(method: string): string {
  const methods: Record<string, string> = {
    CASH: 'Espèces',
    CHECK: 'Chèque',
    BANK_TRANSFER: 'Virement bancaire',
    CREDIT_CARD: 'Carte bancaire',
    ONLINE: 'Paiement en ligne',
  };
  return methods[method] || method;
}

/**
 * Format transfer status label
 */
export function formatTransferStatus(status: string): string {
  const statuses: Record<string, string> = {
    PENDING: 'En attente',
    APPROVED: 'Approuvé',
    REJECTED: 'Rejeté',
  };
  return statuses[status] || status;
}

/**
 * Get status color classes
 */
export function getStatusColor(status: string): string {
  const colors: Record<string, string> = {
    PENDING: 'bg-yellow-100 text-yellow-800',
    APPROVED: 'bg-green-100 text-green-800',
    REJECTED: 'bg-red-100 text-red-800',
  };
  return colors[status] || 'bg-gray-100 text-gray-800';
}

/**
 * Format fee item type label
 */
export function formatFeeItemType(type: FeeItemType): string {
  const types: Record<FeeItemType, string> = {
    REGISTRATION: "Frais d'inscription",
    TUITION: 'Frais de scolarité',
    LIBRARY: 'Frais de bibliothèque',
    LABORATORY: 'Frais de laboratoire',
    INSURANCE: 'Assurance',
    UNIFORM: 'Uniforme',
    TRANSPORT: 'Transport',
    MEALS: 'Restauration',
    ACCOMMODATION: 'Hébergement',
    MATERIALS: 'Matériel pédagogique',
    EXAM: "Frais d'examen",
    OTHER: 'Autres frais',
  };
  return types[type] || type;
}
